import React, { useState, useEffect, useCallback } from 'react';
import { ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import apiService from '../../../apiService';
import { fmtMoney, fmtInt, Card, KpiCard, BarRow, Spinner, usePeriodControls } from '../financeShared';

const METHOD_LABELS = { airtel_money: 'Airtel Money', orange_money: 'Orange Money', serdipay: 'SerdiPay', cash: 'Cash', card: 'Carte' };

export default function TreasuryTab({ currency, rate }) {
  const { params, qs, groupBy, controls } = usePeriodControls('1year', 'month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fmt = (n) => fmtMoney(n, currency, rate);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.get(`/admin/finance/treasury?${qs()}`, { userType: 'admin' });
      if (res?.success) setData(res);
    } catch (e) { console.error('treasury:', e); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const byMethod = data?.byMethod || [];
  const timeline = data?.timeline || [];
  const methodTotal = byMethod.reduce((s, m) => s + m.total, 0);

  return (
    <>
      {controls()}
      {loading ? <Spinner /> : !data ? <p style={{ color: '#9AA0A8' }}>Aucune donnée.</p> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '1.25rem', marginBottom: '2rem' }}>
            <KpiCard label="Encaissements" value={fmt(data.cashIn.total)} color="#10b981" border="#10b981" sub={`${fmtInt(data.cashIn.count)} paiement(s)`} />
            <KpiCard label="Décaissements" value={fmt(data.cashOut.total)} color="#dc2626" border="#dc2626" sub={`${fmtInt(data.cashOut.count)} retrait(s)`} />
            <KpiCard label="Flux net" value={fmt(data.net)} color={data.net >= 0 ? '#10b981' : '#dc2626'} border="#FF7800" sub="Encaissements − décaissements" />
            <KpiCard label="Solde comptes plateforme" value={fmt(data.platform.totalBalance)} color="#FF7800" border="#FF7800" sub={`${fmtInt(data.platform.accounts.length)} compte(s)`} />
          </div>

          <Card style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1.25rem', color: '#1E2229', fontSize: '1.05rem', fontWeight: 600 }}>Encaissements par moyen de paiement</h3>
            {byMethod.length === 0 ? <p style={{ color: '#9AA0A8' }}>Aucun encaissement.</p> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                {byMethod.map((m, i) => {
                  const pct = methodTotal > 0 ? (m.total / methodTotal) * 100 : 0;
                  const colors = ['#10b981', '#FF7800', '#FFB900', '#FF7800', '#06b6d4'];
                  return <BarRow key={m.method} label={METHOD_LABELS[m.method] || m.method} pct={pct} color={colors[i % colors.length]} valueText={`${fmt(m.total)} · ${fmtInt(m.count)}×`} />;
                })}
              </div>
            )}
          </Card>

          <Card>
            <h3 style={{ marginBottom: '1.25rem', color: '#1E2229', fontSize: '1.05rem', fontWeight: 600 }}>Flux de trésorerie ({groupBy === 'month' ? 'par mois' : 'par jour'})</h3>
            {timeline.length === 0 ? <p style={{ color: '#9AA0A8' }}>Aucune donnée.</p> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#FFFFFF', borderBottom: '2px solid #E9EBEF' }}>
                    {['Période', 'Encaissé', 'Décaissé', 'Net'].map(h => (
                      <th key={h} style={{ padding: '0.75rem 1rem', textAlign: h === 'Période' ? 'left' : 'right', fontWeight: 700, color: '#414852', fontSize: '0.8rem', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {timeline.slice().reverse().map((row, idx) => (
                      <tr key={row.period} style={{ borderBottom: '1px solid #E9EBEF', background: idx % 2 === 0 ? '#fff' : '#FFFFFF' }}>
                        <td style={{ padding: '0.7rem 1rem', color: '#1E2229', fontWeight: 600 }}>{row.period}</td>
                        <td style={{ padding: '0.7rem 1rem', textAlign: 'right', color: '#10b981', fontWeight: 600 }}><ArrowDownCircle size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} />{fmt(row.cashIn)}</td>
                        <td style={{ padding: '0.7rem 1rem', textAlign: 'right', color: '#dc2626', fontWeight: 600 }}><ArrowUpCircle size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} />{fmt(row.cashOut)}</td>
                        <td style={{ padding: '0.7rem 1rem', textAlign: 'right', color: row.net >= 0 ? '#10b981' : '#dc2626', fontWeight: 700 }}>{fmt(row.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </>
  );
}
